import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Trophy, Loader2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { StepGame, StepDetails, StepRegistration, StepSettings } from "./FormSteps";
import { EMPTY_TOURNAMENT, FORMAT_LABEL, fmtDateTime } from "./constants";

const STEPS = ["Game", "Tournament Details", "Registration", "Settings", "Review & Create"];

function Review({ f, price }) {
  const rows = [["Game", f.game || "—"], ["Format", FORMAT_LABEL[f.format]], ["Schedule", `${fmtDateTime(f.start_date)} → ${fmtDateTime(f.end_date)}`],
    ["Timezone", f.timezone], ["Max Teams", f.max_teams], ["Players per Team", f.team_size], ["Match Format", `${f.match_format} (Grand Final: ${f.grand_final_format})`],
    ["Third Place Match", f.third_place ? "Yes" : "No"], ["Registration", f.registration_open ? "Open (link + QR)" : "Closed"]];
  return (
    <div>
      <h3 className="text-lg font-bold text-white mb-4">Review & Create</h3>
      {f.cover && <img src={mediaUrl(f.cover)} alt="" className="w-full aspect-[16/6] object-cover rounded-xl mb-4" />}
      <div className="text-xl font-bold text-white">{f.name || "Untitled tournament"}</div>
      <p className="text-sm text-white/50 mt-1 mb-4 line-clamp-3">{f.description}</p>
      <div className="divide-y divide-white/5 text-sm">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between py-2"><span className="text-white/50">{k}</span><span className="font-semibold text-white text-right">{String(v)}</span></div>)}
      </div>
      {price > 0 && <div className="mt-5 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 p-4 text-sm text-white/80" data-testid="wiz-price-note">Creating a tournament costs <b className="text-white">${price.toFixed(2)}</b>. You'll complete a secure payment in the next step; the tournament goes public right after.</div>}
    </div>
  );
}

export default function TournamentWizard({ onCancel, onCreated, price = 0 }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY_TOURNAMENT);
  const [games, setGames] = useState([]);
  const [busy, setBusy] = useState(false);
  const upd = (p) => setF((cur) => ({ ...cur, ...p }));
  useEffect(() => { api.get("/games").then((r) => setGames(r.data.items)).catch(() => {}); }, []);

  const next = () => {
    if (step === 1 && f.name.trim().length < 2) { toast.error("Tournament name is required"); return; }
    setStep(step + 1);
  };
  const create = async () => {
    if (f.name.trim().length < 2) { toast.error("Tournament name is required"); setStep(1); return; }
    setBusy(true);
    try { const r = await api.post("/manage/tournaments", f); onCreated(r.data); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Please check the form fields"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-8" data-testid="tournament-wizard">
      <ol className="space-y-1">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${i === step ? "bg-white/5" : ""}`}>
            <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${i < step ? "bg-green-500/20 text-green-400" : i === step ? "bg-[hsl(var(--primary))] text-white" : "bg-white/10 text-white/40"}`}>{i < step ? <Check size={14} /> : i + 1}</span>
            <span className={`text-sm font-semibold ${i === step ? "text-white" : "text-white/40"}`}>{s}</span>
          </li>
        ))}
      </ol>
      <div>
        <div className="eyebrow text-white/40 mb-2">Step {step + 1} of {STEPS.length}</div>
        <div className="card-surface rounded-2xl p-6 min-h-[360px]">
          {step === 0 && <StepGame f={f} upd={upd} games={games} />}
          {step === 1 && <StepDetails f={f} upd={upd} />}
          {step === 2 && <StepRegistration f={f} upd={upd} />}
          {step === 3 && <StepSettings f={f} upd={upd} />}
          {step === 4 && <Review f={f} price={price} />}
        </div>
        <div className="flex justify-between mt-5">
          <button type="button" onClick={() => (step === 0 ? onCancel() : setStep(step - 1))} data-testid="wiz-back" className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5"><ChevronLeft size={16} /> {step === 0 ? "Cancel" : "Back"}</button>
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next} data-testid="wiz-next" className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold hover:brightness-110">Next <ChevronRight size={16} /></button>
          ) : (
            <button type="button" onClick={create} disabled={busy} data-testid="wiz-create" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold disabled:opacity-60 hover:brightness-110">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Trophy size={16} />} {price > 0 ? `Create & Pay $${price.toFixed(2)}` : "Create Tournament"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
